<template>
  <v-card outlined class="sales-rep-card">
    <v-card-text class="pa-4">
      <div class="sales-rep-header">
        <div>
          <div class="section-title">Sales By Rep</div>
          <div class="section-subtitle">
            {{ invoices.length }} rep{{ invoices.length === 1 ? "" : "s" }}
          </div>
        </div>

        <DateTimePicker
          defaultRange="since7DaysAgo"
          :range="true"
          v-model="dateTime"
          @submit="getAll()"
        />
      </div>

      <div class="total-box mt-4">
        <div>
          <div class="total-label">Total Sales</div>
          <div class="total-value">{{ total | currency }}</div>
        </div>

        <v-icon color="green darken-2">mdi-chart-line</v-icon>
      </div>

      <v-skeleton-loader
        v-if="isLoading"
        type="list-item-avatar-two-line, list-item-avatar-two-line, list-item-avatar-two-line"
        class="mt-4"
      />

      <div v-else-if="invoices.length > 0" class="rep-list mt-4">
        <div
          v-for="invoice in invoices"
          :key="invoice.id || invoice.name"
          class="rep-row"
          @click="viewReport(invoice)"
        >
          <div class="rep-main">
            <v-avatar size="42">
              <v-img
                v-if="invoice.imageUrl"
                :src="invoice.imageUrl"
              />

              <v-img
                v-else
                src="@/assets/images/user.jpeg"
              />
            </v-avatar>

            <div class="rep-info">
              <div class="rep-name">
                {{ invoice.name || "Not selected" }}
              </div>

              <div class="rep-subtitle">
                Click to view report
              </div>
            </div>
          </div>

          <div class="rep-sale">
            {{ invoice.totalSale | currency }}
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <lottie
          :height="150"
          :width="150"
          :options="defaultOptions"
          v-on:animCreated="handleAnimation"
        />

        <div class="empty-title">No invoices found</div>
        <div class="empty-subtitle">Try changing the date range.</div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import invoiceService from "@/modules/Invoice/service";
import Lottie from "@/components/lottie.vue";
import * as animationData from "@/assets/lottie/13625-list.json";

const DateTimePicker = () => import("@/components/DateTimePicker");

export default {
  name: "InvoiceBySalesRep",

  components: {
    DateTimePicker,
    Lottie,
  },

  data() {
    return {
      isLoading: true,
      invoices: [],
      dateTime: {
        start: this.$moment().subtract(7, "days").startOf("day").toISOString(),
        end: this.$moment().endOf("day").toISOString(),
      },
      defaultOptions: {
        loop: true,
        autoplay: true,
        animationData: animationData.default,
      },
    };
  },

  computed: {
    total() {
      return this.invoices.reduce((sum, row) => {
        return sum + Number(row.totalSale || 0);
      }, 0);
    },
  },

  mounted() {
    this.getAll();
  },

  methods: {
    viewReport(invoice) {
      const id = invoice.id || 0;

      const routeData = this.$router.resolve({
        path: `/global/salesman/${id}/view?start=${this.dateTime.start}&end=${this.dateTime.end}`,
      });

      window.open(routeData.href, "newwindow", "width=850, height=1100");
    },

    handleAnimation(anim) {
      this.anim = anim;
    },

    async getAll() {
      this.isLoading = true;

      const where = {
        start: this.dateTime.start,
        end: this.dateTime.end,
      };

      try {
        const result = await invoiceService.invoiceBySalesRep(where);
        this.invoices = result.data || [];
      } catch (error) {
        console.error("invoiceBySalesRep failed", error);
        this.invoices = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.sales-rep-card {
  border-radius: 20px;
  background: #ffffff;
}

.sales-rep-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
}

.section-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.total-box {
  background: #f8fafc;
  border-radius: 16px;
  padding: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.total-label {
  font-size: 12px;
  color: #64748b;
}

.total-value {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.rep-list {
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}

.rep-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.rep-row:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.rep-main {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.rep-info {
  min-width: 0;
}

.rep-name {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  text-transform: capitalize;
}

.rep-subtitle {
  font-size: 12px;
  color: #64748b;
  margin-top: 2px;
}

.rep-sale {
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
}

.empty-state {
  text-align: center;
  padding: 24px 0;
}

.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.empty-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 4px;
}

@media (max-width: 600px) {
  .rep-row {
    align-items: flex-start;
  }

  .rep-sale {
    font-size: 14px;
  }
}
</style>